import { useNavigate } from 'react-router'
import type { BookingListItem, BookingListProps } from '@/lib/render'
import { Badge, Card, statusTone } from '@/components/data/ui'
import { PaymentMark } from './calendar/shared'
import { fmtDayDate, fmtMoney, fmtTime, fmtCount, displayName } from '@/lib/format'
import { useRole } from '@/hooks/useRole'
import { cn } from '@/lib/utils'

interface Props {
  props: unknown
  /**
   * Rows become links to the booking record. OFF by default for the same reason
   * the calendar's is: these renderers also draw customer-facing chat, and
   * /booking/:id is an operator screen.
   */
  interactive?: boolean
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Rows arrive newest-first from list_bookings; group them under date headings. */
function groupByDate(bookings: BookingListItem[]): [string, BookingListItem[]][] {
  const groups = new Map<string, BookingListItem[]>()
  for (const b of bookings) {
    const day = String(b.start ?? '').slice(0, 10)
    const bucket = groups.get(day)
    if (bucket) bucket.push(b)
    else groups.set(day, [b])
  }
  return Array.from(groups.entries())
}

export function BookingListRenderer({ props, interactive }: Props) {
  const navigate = useNavigate()
  const { userRole } = useRole()
  const data = props as BookingListProps

  if (!Array.isArray(data?.bookings)) {
    return (
      <div className="rounded-lg bg-destructive/10 p-4">
        <p className="text-[13px] text-destructive">Invalid booking list data</p>
      </div>
    )
  }

  const currency = data.currency ?? 'AUD'
  const operator = userRole === 'owner' || userRole === 'staff'
  // A synthetic id would make a dead link, so only real booking uuids open.
  const canOpen = (b: BookingListItem) => Boolean(interactive) && operator && UUID_RE.test(String(b.id ?? ''))
  const groups = groupByDate(data.bookings)
  const shown = data.bookings.length
  const total = typeof data.total === 'number' ? data.total : shown

  return (
    <Card className="gap-0 overflow-hidden py-0">
      {data.title ? (
        <p className="border-b border-border bg-muted px-4 py-2.5 text-sm font-semibold text-foreground">
          {data.title}
        </p>
      ) : null}

      {shown === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">No appointments.</p>
      ) : (
        groups.map(([day, rows]) => (
          <div key={day}>
            <p className="border-b border-border bg-muted/50 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {fmtDayDate(rows[0].start)}
            </p>
            {rows.map((b) => {
              const owing = Number(b.balance_due ?? 0)
              const cancelled = b.status === 'cancelled'
              const clickable = canOpen(b)
              return (
                <div
                  key={b.id}
                  {...(clickable
                    ? {
                      role: 'link' as const,
                      tabIndex: 0,
                      onClick: () => navigate(`/booking/${b.id}`),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(`/booking/${b.id}`)
                        }
                      },
                    }
                    : {})}
                  className={cn(
                    'flex items-start gap-3 border-b border-border px-4 py-2.5 last:border-b-0',
                    cancelled && 'opacity-60',
                    clickable && 'cursor-pointer hover:bg-accent',
                  )}
                >
                  <span className="w-[46px] shrink-0 pt-px text-[12.5px] font-semibold tabular-nums text-foreground">
                    {fmtTime(b.start)}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-[13.5px] font-semibold text-foreground', cancelled && 'line-through')}>
                      {b.service_name}
                    </p>
                    <p className="truncate text-[11.5px] text-muted-foreground">
                      {[
                        b.customer_name ? displayName(b.customer_name) : null,
                        b.resource_name || null,
                      ].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <div className="flex items-center gap-1.5">
                      {b.payment_status ? (
                        <PaymentMark status={b.payment_status} byPackage={b.paid_with_package} />
                      ) : null}
                      <Badge label={b.status.replace(/_/g, ' ')} tone={statusTone(b.status)} />
                    </div>
                    {owing > 0 && !cancelled ? (
                      <span className="text-[11px] font-semibold text-foreground/80">
                        {fmtMoney(owing, currency)} owing
                      </span>
                    ) : typeof b.price === 'number' ? (
                      <span className="text-[11px] text-muted-foreground">{fmtMoney(b.price, currency)}</span>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        ))
      )}

      {shown > 0 ? (
        <p className="border-t border-border bg-muted px-3 py-1.5 text-[11px] text-muted-foreground">
          {total > shown
            ? `Showing ${fmtCount(shown)} of ${fmtCount(total)} appointments`
            : `${fmtCount(shown)} appointment${shown === 1 ? '' : 's'}`}
        </p>
      ) : null}
    </Card>
  )
}
